import { DeviceGeneralType } from './deviceTypes';

export type DeviceClassification = {
  /** Campo `type` da API: granularidade de hardware (`OBD`, `Dashcam`, `iButton`…). */
  type?: string | null;
  /** Campo `generalType` da API: categoria de instalação (`INSTALLED`, `REMOVABLE`…). */
  generalType?: string | null;
};

/**
 * Categoria inferida a partir do `type`, usada só quando o `generalType` não classifica.
 *
 * `OBD` aqui é nome de família de hardware (LMU/FM), não de instalação: esses aparelhos vão
 * no chicote, então a categoria é `INSTALLED`. O mapa antigo devolvia `'OBD'` e, como esse
 * valor não existe no enum de categorias, o veículo sumia de qualquer filtro de tipo.
 */
const CATEGORY_BY_DEVICE_TYPE: Record<string, DeviceGeneralType> = {
  BLOCKER: DeviceGeneralType.BLOCKER,
  BUZZER: DeviceGeneralType.INSTALLED,
  CAN: DeviceGeneralType.CAN,
  CANIBUTTON: DeviceGeneralType.CAN_IBUTTON,
  CANIBUTTONIMMOBILLIZER: DeviceGeneralType.CAN_IBUTTON_IMMOBILIZER,
  DASHCAM: DeviceGeneralType.DASHCAM,
  IBUTTON: DeviceGeneralType.IBUTTON,
  IBUTTONIMMOBILIZER: DeviceGeneralType.IBUTTON_IMMOBILIZER,
  IMMOBILIZER: DeviceGeneralType.IMMOBILIZER,
  MULTICAM: DeviceGeneralType.MULTICAM,
  OBD: DeviceGeneralType.INSTALLED,
  OBD_REMOVABLE: DeviceGeneralType.REMOVABLE,
  PLUGANDPLAYBUZZER: DeviceGeneralType.REMOVABLE,
  PLUGANDPLAYIBUTTON: DeviceGeneralType.PLUG_AND_PLAY_IBUTTON,
  REMOVABLE: DeviceGeneralType.REMOVABLE,
  SOLAR: DeviceGeneralType.INSTALLED,
};

const categoryFromGeneralType = (generalType?: string | null): DeviceGeneralType | undefined => {
  if (!generalType) return undefined;

  const category = DeviceGeneralType[generalType.toUpperCase() as keyof typeof DeviceGeneralType];

  // `UNKNOWN` não classifica — deixa o `type` tentar (é o caso do ADR 0008: a ADPLUS chegou
  // com uma das duas classificações vazia). `OBD` não é categoria de instalação e o schema
  // não devolve esse valor em `generalType`; se devolver, o mapa por `type` decide.
  if (category === DeviceGeneralType.UNKNOWN || category === DeviceGeneralType.OBD) return undefined;

  return category;
};

/**
 * Resolve a categoria de instalação que o cliente enxerga (Instalado, Removível, Câmera,
 * Rede CAN…) a partir das DUAS classificações que a API devolve, que não são intercambiáveis:
 *
 * - `generalType` (categoria): como o aparelho foi instalado. É a fonte da verdade.
 * - `type` (granular): vem de um mapa modelo→enum no BFF, que atrasa sempre que uma nova
 *   família de hardware entra na frota. Um LMU2630 no chicote chega como `OBD`, que descreve
 *   o hardware e não a instalação.
 *
 * Precedência: `generalType` manda; o `type` só entra quando o primeiro não classifica.
 *
 * Esta é a única função que decide essa precedência — telas não devem reimplementar o mapa.
 * Ver docs/adr/0015-classificacao-de-dispositivo-tem-um-dono-so.md.
 */
export const resolveDeviceCategory = ({ type, generalType }: DeviceClassification): DeviceGeneralType => {
  const fromGeneralType = categoryFromGeneralType(generalType);
  if (fromGeneralType) return fromGeneralType;

  const fromType = type ? CATEGORY_BY_DEVICE_TYPE[type.toUpperCase()] : undefined;

  return fromType ?? DeviceGeneralType.UNKNOWN;
};
